import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  Download,
  History,
  RotateCcw,
  Save,
  Search,
  X,
} from 'lucide-react';
import {
  getRestaurantStock,
  getInventorySettings,
  createInventoryCount,
  getInventoryCounts,
  getInventoryCount,
} from '../services/api';
import {
  MAX_LINES_PER_COUNT,
  draftStorageKey,
  isCountable,
  groupItemsByCategory,
  lineState,
  formatQuantity,
  countProgress,
  invalidEntries,
  buildCountPayload,
  linesWithDifference,
  retryLines,
  topVariances,
  serializeDraft,
  parseDraft,
  countLinesToCsv,
  countCsvFileName,
  differenceKind,
} from '../utils/inventoryCount';
import { downloadCsv } from '../utils/kardex';
import { formatCurrency } from '../utils/formatCurrency';
import { storage } from '../utils/storage';
import { trackEvent } from '../utils/analytics';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import useEscapeKey from '../hooks/useEscapeKey';
import './InventoryCountPage.css';

const HISTORY_PAGE_SIZE = 20;

const KNOWN_ERROR_CODES = [
  'ITEM_NOT_FOUND',
  'STOCK_NOT_TRACKED',
  'DUPLICATE_ITEM',
  'INVALID_NUMBER',
];

const CSV_COLUMNS = [
  'item',
  'category',
  'unit',
  'theoretical',
  'counted',
  'difference',
  'unitCost',
  'differenceValue',
  'reason',
];

// `storage` es localStorage: en modo privado o con la cuota llena puede tirar
const safeStorage = {
  get: (key) => {
    try {
      return storage.get(key);
    } catch {
      return null;
    }
  },
  set: (key, value) => {
    try {
      storage.set(key, value);
      return true;
    } catch {
      return false;
    }
  },
  remove: (key) => {
    try {
      storage.remove(key);
    } catch {
      // nada que hacer
    }
  },
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

const kindClass = (value) => {
  const kind = differenceKind(value);
  return kind ? `diff-${kind}` : '';
};

/**
 * Resumen de varianza de un conteo: totales en unidades y en $, y el top 5 de diferencias.
 * Se usa después de finalizar un conteo y en el detalle del historial.
 * @param {{count: Object, t: Function}} props - Conteo y traductor
 * @returns {JSX.Element} Resumen
 */
function CountSummary({ count, t }) {
  const summary = count?.summary || {};
  const top = topVariances(count);
  const net = Number(summary.netValue) || 0;

  return (
    <div className="count-summary">
      <div className="count-summary-cards">
        <div className="count-card">
          <span className="count-card-label">{t('inventory.count.summary.itemsCounted')}</span>
          <strong className="count-card-value">{summary.itemsCounted ?? 0}</strong>
        </div>
        <div className="count-card">
          <span className="count-card-label">
            {t('inventory.count.summary.itemsWithDifference')}
          </span>
          <strong className="count-card-value">{summary.itemsWithDifference ?? 0}</strong>
        </div>
        <div className="count-card count-card--shortage">
          <span className="count-card-label">{t('inventory.count.summary.shortage')}</span>
          <strong className="count-card-value diff-shortage">
            {formatCurrency(Math.abs(Number(summary.shortageValue) || 0))}
          </strong>
        </div>
        <div className="count-card count-card--surplus">
          <span className="count-card-label">{t('inventory.count.summary.surplus')}</span>
          <strong className="count-card-value diff-surplus">
            {formatCurrency(Math.abs(Number(summary.surplusValue) || 0))}
          </strong>
        </div>
        <div className="count-card">
          <span className="count-card-label">{t('inventory.count.summary.net')}</span>
          <strong className={`count-card-value ${kindClass(net)}`}>{formatCurrency(net)}</strong>
        </div>
      </div>

      <h3 className="count-section-title">{t('inventory.count.summary.topTitle')}</h3>
      {top.length === 0 ? (
        <p className="count-muted">{t('inventory.count.summary.noDifferences')}</p>
      ) : (
        <div className="count-table-wrapper">
          <table className="count-table count-top-table">
            <thead>
              <tr>
                <th>{t('inventory.count.columns.item')}</th>
                <th className="num">{t('inventory.count.columns.difference')}</th>
                <th className="num">{t('inventory.count.columns.differenceValue')}</th>
              </tr>
            </thead>
            <tbody>
              {top.map((line) => (
                <tr key={line.itemId} className="count-top-row">
                  <td>{line.name || line.itemId}</td>
                  <td className={`num ${kindClass(line.difference)}`}>
                    {formatQuantity(line.difference, { signed: true })} {line.unit || ''}
                  </td>
                  <td className={`num ${kindClass(line.differenceValue)}`}>
                    {line.differenceValue === null || line.differenceValue === undefined
                      ? '—'
                      : formatCurrency(line.differenceValue)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Conteo de inventario guiado con varianza (C3 / RES-40): el encargado cuenta por categoría, ve
 * la diferencia contra el stock teórico mientras carga, puede guardar parcial y retomar (hasta
 * 7 días), y al finalizar el backend ajusta el stock y devuelve el resumen de varianza. En la
 * pestaña de historial están los conteos anteriores con su detalle y export a CSV.
 * @returns {JSX.Element} Página
 */
function InventoryCountPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const draftKey = draftStorageKey(safeStorage.get('selectedRestaurantId'));

  // --- Conteo en curso ---
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  // RES-28: el backend dice si este usuario puede ajustar stock. Ante la duda se muestra el
  // conteo y el 403 del POST explica por qué no.
  const [canWrite, setCanWrite] = useState(true);
  const [entries, setEntries] = useState({});
  const [note, setNote] = useState('');
  const [dirty, setDirty] = useState(false);
  const [pendingDraft, setPendingDraft] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [search, setSearch] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [feedback, setFeedback] = useState(null);
  // { count, sentLines }: respuesta del último POST y las líneas que se mandaron (para reintentar)
  const [result, setResult] = useState(null);
  const startedRef = useRef(false);
  const listRef = useRef(null);

  // --- Historial ---
  const [history, setHistory] = useState([]);
  const [historyNextToken, setHistoryNextToken] = useState(null);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(false);
  const [detail, setDetail] = useState(null); // { loading, error, count }

  const tab = !canWrite || searchParams.get('tab') === 'history' ? 'history' : 'count';

  const hasUnsaved = dirty && Object.keys(entries).length > 0;
  const { showModal, confirmNavigation, cancelNavigation, guardNavigation } =
    useUnsavedChanges(hasUnsaved);

  useEscapeKey(showModal, cancelNavigation);
  useEscapeKey(confirmOpen && !submitting, () => setConfirmOpen(false));
  useEscapeKey(Boolean(detail), () => setDetail(null));

  const showFeedback = useCallback((type, message) => {
    setFeedback({ type, message });
  }, []);

  useEffect(() => {
    if (!feedback) return undefined;
    const timer = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(timer);
  }, [feedback]);

  const loadStock = useCallback(async () => {
    try {
      const response = await getRestaurantStock();
      setItems(response?.items || []);
      setLoadError(false);
    } catch (error) {
      console.error('Error loading stock for count:', error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getInventorySettings()
      .then((settings) => {
        if (settings?.can) setCanWrite(settings.can.stockWrite !== false);
      })
      .catch(() => {});
    loadStock();
  }, [loadStock]);

  // Borrador guardado de este local: se ofrece retomarlo; si venció se borra
  useEffect(() => {
    const draft = parseDraft(safeStorage.get(draftKey));
    if (draft?.expired) {
      safeStorage.remove(draftKey);
    } else if (draft) {
      setPendingDraft(draft);
    }
  }, [draftKey]);

  const countableItems = useMemo(() => items.filter(isCountable), [items]);
  const groups = useMemo(() => groupItemsByCategory(items), [items]);
  const itemsById = useMemo(() => new Map(items.map((item) => [item.itemId, item])), [items]);

  const visibleGroups = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return groups;
    return groups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => (item.name || '').toLowerCase().includes(query)),
      }))
      .filter((group) => group.items.length > 0);
  }, [groups, search]);

  const overall = useMemo(() => countProgress(countableItems, entries), [countableItems, entries]);
  const invalidIds = useMemo(
    () => invalidEntries(countableItems, entries),
    [countableItems, entries]
  );
  const payload = useMemo(
    () => buildCountPayload(countableItems, entries, note),
    [countableItems, entries, note]
  );

  const markStarted = (resumed) => {
    if (startedRef.current) return;
    startedRef.current = true;
    trackEvent('inventory_count_started', { resumed });
  };

  const updateEntry = (itemId, field, value) => {
    setEntries((prev) => ({ ...prev, [itemId]: { ...prev[itemId], [field]: value } }));
    setDirty(true);
    setSubmitError(null);
    markStarted(false);
  };

  const handleNoteChange = (value) => {
    setNote(value);
    setDirty(true);
  };

  const handleResumeDraft = () => {
    if (!pendingDraft) return;
    setEntries(pendingDraft.entries || {});
    setNote(pendingDraft.note || '');
    setPendingDraft(null);
    setDirty(false);
    setResult(null);
    markStarted(true);
  };

  const handleDiscardDraft = () => {
    safeStorage.remove(draftKey);
    setPendingDraft(null);
  };

  const saveDraft = () => {
    const ok = safeStorage.set(draftKey, serializeDraft({ entries, note }));
    if (ok) {
      setDirty(false);
      setPendingDraft(null);
      showFeedback('success', t('inventory.count.draft.saved'));
    } else {
      showFeedback('error', t('inventory.count.draft.saveError'));
    }
    return ok;
  };

  const toggleGroup = (category) => {
    setCollapsed((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  // Enter en "conteo real" pasa al siguiente insumo: se cuenta de corrido sin mouse
  const handleCountedKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    const inputs = [...(listRef.current?.querySelectorAll('input.count-input') || [])];
    const next = inputs[inputs.indexOf(event.target) + 1];
    if (next) next.focus();
  };

  const setTab = (next) => {
    setSearchParams(next === 'history' ? { tab: 'history' } : {});
  };

  const errorMessage = (error) => {
    const status = error?.response?.status;
    const data = error?.response?.data || {};
    if (status === 403) return t('inventory.count.errors.FORBIDDEN');
    const base = KNOWN_ERROR_CODES.includes(data.code)
      ? t(`inventory.count.errors.${data.code}`)
      : t('inventory.count.errors.generic');
    const name = data.itemId ? itemsById.get(data.itemId)?.name || data.itemId : null;
    return name ? `${base} (${name})` : base;
  };

  const sendCount = async (lines, countNote) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const body = { lines };
      if (countNote) body.note = countNote;
      const count = await createInventoryCount(body);
      const summary = count?.summary || {};
      trackEvent('inventory_count_submitted', {
        items: summary.itemsCounted ?? lines.length,
        withDifference: summary.itemsWithDifference ?? 0,
      });

      // Si quedó PARTIAL, lo que falló sigue cargado (y guardado) para poder reintentar después
      const failed = count?.status === 'PARTIAL' ? count.failedItemIds || [] : [];
      if (failed.length > 0) {
        const failedEntries = {};
        failed.forEach((itemId) => {
          const line = lines.find((l) => l.itemId === itemId);
          if (line) {
            failedEntries[itemId] = { counted: String(line.counted), reason: line.reason || '' };
          }
        });
        setEntries(failedEntries);
        safeStorage.set(draftKey, serializeDraft({ entries: failedEntries, note: countNote }));
      } else {
        setEntries({});
        setNote('');
        safeStorage.remove(draftKey);
        startedRef.current = false;
      }
      setDirty(false);
      setPendingDraft(null);
      setResult({ count, sentLines: lines });
      setConfirmOpen(false);
      setHistoryLoaded(false);
      // El teórico cambió: se recarga para el próximo conteo
      loadStock();
    } catch (error) {
      console.error('Error creating inventory count:', error);
      setSubmitError(errorMessage(error));
      setConfirmOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenConfirm = () => {
    if (invalidIds.length > 0) {
      setSubmitError(t('inventory.count.validation.invalid', { count: invalidIds.length }));
      return;
    }
    if (payload.lines.length > MAX_LINES_PER_COUNT) {
      setSubmitError(
        t('inventory.count.validation.tooMany', {
          max: MAX_LINES_PER_COUNT,
          count: payload.lines.length,
        })
      );
      return;
    }
    if (payload.lines.length === 0) return;
    setSubmitError(null);
    setConfirmOpen(true);
  };

  const handleConfirmSubmit = () => sendCount(payload.lines, payload.note);

  const handleRetryFailed = () => {
    if (!result) return;
    const lines = retryLines(result.sentLines, result.count?.failedItemIds);
    if (lines.length > 0) sendCount(lines, result.count?.note || '');
  };

  const exportCount = (count) => {
    const headers = CSV_COLUMNS.map((column) => t(`inventory.count.csv.${column}`));
    downloadCsv(countLinesToCsv(count?.lines || [], headers), countCsvFileName(count));
  };

  // --- Historial ---
  const loadHistory = useCallback(async (token = null) => {
    setHistoryLoading(true);
    try {
      const params = { limit: HISTORY_PAGE_SIZE };
      if (token) params.nextToken = token;
      const response = await getInventoryCounts(params);
      const page = response?.items || [];
      setHistory((prev) => (token ? [...prev, ...page] : page));
      setHistoryNextToken(response?.nextToken || null);
      setHistoryError(false);
    } catch (error) {
      console.error('Error loading inventory counts:', error);
      setHistoryError(true);
    } finally {
      setHistoryLoading(false);
      setHistoryLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (tab === 'history' && !historyLoaded && !historyLoading) loadHistory();
  }, [tab, historyLoaded, historyLoading, loadHistory]);

  const openDetail = async (countId) => {
    setDetail({ loading: true, countId });
    try {
      const count = await getInventoryCount(countId);
      setDetail((prev) => (prev && prev.countId === countId ? { countId, count } : prev));
    } catch (error) {
      console.error('Error loading inventory count:', error);
      setDetail((prev) => (prev && prev.countId === countId ? { countId, error: true } : prev));
    }
  };

  // --- Render ---
  const statusBadge = (status) => (
    <span className={`count-badge count-badge--${(status || 'COMPLETED').toLowerCase()}`}>
      {t(`inventory.count.status.${status || 'COMPLETED'}`)}
    </span>
  );

  const renderDraftBanner = () =>
    pendingDraft && (
      <div className="count-draft-banner" role="status">
        <span>
          {t('inventory.count.draft.banner', {
            date: formatDateTime(new Date(pendingDraft.savedAt).toISOString()),
            count: Object.keys(pendingDraft.entries || {}).length,
          })}
        </span>
        <div className="count-draft-actions">
          <button type="button" className="btn btn-primary btn-sm" onClick={handleResumeDraft}>
            <RotateCcw size={14} />
            {t('inventory.count.draft.resume')}
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={handleDiscardDraft}>
            {t('inventory.count.draft.discard')}
          </button>
        </div>
      </div>
    );

  const renderResult = () => {
    const { count } = result;
    const failedIds = count?.status === 'PARTIAL' ? count.failedItemIds || [] : [];
    return (
      <div className="count-result">
        <div className="count-result-header">
          <h2>{t('inventory.count.summary.title')}</h2>
          {statusBadge(count?.status)}
        </div>
        <CountSummary count={count} t={t} />

        {failedIds.length > 0 && (
          <div className="count-partial" role="alert">
            <h3>{t('inventory.count.partial.title')}</h3>
            <p>{t('inventory.count.partial.body')}</p>
            <ul className="count-failed-list">
              {failedIds.map((itemId) => (
                <li key={itemId}>{itemsById.get(itemId)?.name || itemId}</li>
              ))}
            </ul>
            <button
              type="button"
              className="btn btn-warning count-retry-btn"
              onClick={handleRetryFailed}
              disabled={submitting}
            >
              <RotateCcw size={16} />
              {submitting
                ? t('inventory.count.submitting')
                : t('inventory.count.actions.retryFailed', { count: failedIds.length })}
            </button>
          </div>
        )}

        <div className="count-result-actions">
          <button type="button" className="btn btn-secondary" onClick={() => exportCount(count)}>
            <Download size={16} />
            {t('inventory.count.actions.exportCsv')}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => setTab('history')}>
            <History size={16} />
            {t('inventory.count.actions.viewHistory')}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setResult(null)}>
            <ClipboardCheck size={16} />
            {failedIds.length > 0
              ? t('inventory.count.actions.backToCount')
              : t('inventory.count.actions.newCount')}
          </button>
        </div>
      </div>
    );
  };

  const renderGroup = (group) => {
    const progress = countProgress(
      groups.find((g) => g.category === group.category)?.items || group.items,
      entries
    );
    const isCollapsed = Boolean(collapsed[group.category]);
    const label = group.category || t('inventory.count.uncategorized');
    const done = progress.total > 0 && progress.counted === progress.total;

    return (
      <section key={group.category || '__none'} className="count-group">
        <button
          type="button"
          className={`count-group-header${done ? ' is-done' : ''}`}
          onClick={() => toggleGroup(group.category)}
          aria-expanded={!isCollapsed}
        >
          {isCollapsed ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
          <span className="count-group-name">{label}</span>
          <span className="count-group-progress">{t('inventory.count.progress', progress)}</span>
          <span className="count-progress-bar" aria-hidden="true">
            <span
              className="count-progress-fill"
              style={{
                width: `${progress.total ? (progress.counted / progress.total) * 100 : 0}%`,
              }}
            />
          </span>
        </button>

        {!isCollapsed && (
          <div className="count-table-wrapper">
            <table className="count-table">
              <thead>
                <tr>
                  <th>{t('inventory.count.columns.item')}</th>
                  <th className="num">{t('inventory.count.columns.theoretical')}</th>
                  <th>{t('inventory.count.columns.counted')}</th>
                  <th className="num">{t('inventory.count.columns.difference')}</th>
                  <th>{t('inventory.count.columns.reason')}</th>
                </tr>
              </thead>
              <tbody>
                {group.items.map((item) => {
                  const entry = entries[item.itemId] || {};
                  const state = lineState(item, entry);
                  const inputId = `count-${item.itemId}`;
                  return (
                    <tr key={item.itemId} className="count-row" data-item-id={item.itemId}>
                      <td className="count-item-name">
                        <label htmlFor={inputId}>{item.name}</label>
                      </td>
                      <td className="num">
                        {formatQuantity(item.stock)} <span className="count-unit">{item.unit}</span>
                      </td>
                      <td>
                        <div className="count-input-wrap">
                          <input
                            id={inputId}
                            type="text"
                            inputMode="decimal"
                            autoComplete="off"
                            className={`count-input${state.valid ? '' : ' has-error'}`}
                            value={entry.counted ?? ''}
                            placeholder={t('inventory.count.countedPlaceholder')}
                            aria-invalid={!state.valid}
                            onChange={(e) => updateEntry(item.itemId, 'counted', e.target.value)}
                            onKeyDown={handleCountedKeyDown}
                          />
                          <span className="count-unit">{item.unit}</span>
                        </div>
                        {!state.valid && (
                          <span className="count-field-error">
                            {t('inventory.count.invalidNumber')}
                          </span>
                        )}
                      </td>
                      <td className={`num count-diff ${state.kind ? `diff-${state.kind}` : ''}`}>
                        {state.difference === null ? (
                          '—'
                        ) : (
                          <>
                            <span>
                              {formatQuantity(state.difference, { signed: true })} {item.unit}
                            </span>
                            {state.kind !== 'even' && (
                              <span className="count-diff-label">
                                {t(`inventory.count.diff.${state.kind}`)}
                                {state.value !== null && ` · ${formatCurrency(state.value)}`}
                              </span>
                            )}
                          </>
                        )}
                      </td>
                      <td>
                        <input
                          type="text"
                          className="count-reason"
                          value={entry.reason ?? ''}
                          maxLength={200}
                          placeholder={t('inventory.count.reasonPlaceholder')}
                          aria-label={t('inventory.count.reasonAria', { name: item.name })}
                          onChange={(e) => updateEntry(item.itemId, 'reason', e.target.value)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    );
  };

  const renderCountTab = () => {
    if (result) return renderResult();
    if (loading) return <p className="count-muted">{t('inventory.count.loading')}</p>;
    if (loadError) return <div className="alert alert-error">{t('inventory.count.loadError')}</div>;
    if (countableItems.length === 0) {
      return <p className="count-muted count-empty">{t('inventory.count.empty')}</p>;
    }

    return (
      <>
        {renderDraftBanner()}

        <div className="count-toolbar">
          <div className="count-search">
            <Search size={16} />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('inventory.count.searchPlaceholder')}
              aria-label={t('inventory.count.searchPlaceholder')}
            />
          </div>
          <span className="count-overall-progress">
            {t('inventory.count.overallProgress', overall)}
          </span>
        </div>

        <div className="count-groups" ref={listRef}>
          {visibleGroups.map(renderGroup)}
        </div>

        <div className="count-footer">
          <div className="count-note">
            <label htmlFor="count-note">{t('inventory.count.note.label')}</label>
            <textarea
              id="count-note"
              rows={2}
              maxLength={500}
              value={note}
              placeholder={t('inventory.count.note.placeholder')}
              onChange={(e) => handleNoteChange(e.target.value)}
            />
          </div>
          {submitError && (
            <div className="alert alert-error count-submit-error" role="alert">
              {submitError}
            </div>
          )}
          <div className="count-footer-actions">
            <button
              type="button"
              className="btn btn-secondary count-save-draft"
              onClick={saveDraft}
              disabled={Object.keys(entries).length === 0 && !note}
            >
              <Save size={16} />
              {t('inventory.count.actions.saveDraft')}
            </button>
            <button
              type="button"
              className="btn btn-primary count-submit"
              onClick={handleOpenConfirm}
              disabled={payload.lines.length === 0 || submitting}
            >
              <ClipboardCheck size={16} />
              {t('inventory.count.actions.submit', { count: payload.lines.length })}
            </button>
          </div>
        </div>
      </>
    );
  };

  const renderHistoryTab = () => (
    <div className="count-history">
      {!canWrite && <div className="count-readonly-note">{t('inventory.count.readOnly')}</div>}
      {historyError && (
        <div className="alert alert-error">{t('inventory.count.history.loadError')}</div>
      )}
      {historyLoaded && !historyError && history.length === 0 && (
        <p className="count-muted count-empty">{t('inventory.count.history.empty')}</p>
      )}
      {history.length > 0 && (
        <div className="count-table-wrapper">
          <table className="count-table count-history-table">
            <thead>
              <tr>
                <th>{t('inventory.count.history.date')}</th>
                <th>{t('inventory.count.history.status')}</th>
                <th className="num">{t('inventory.count.history.items')}</th>
                <th className="num">{t('inventory.count.history.withDifference')}</th>
                <th className="num">{t('inventory.count.history.net')}</th>
                <th>{t('inventory.count.history.note')}</th>
                <th aria-label={t('inventory.count.actions.viewDetail')} />
              </tr>
            </thead>
            <tbody>
              {history.map((count) => {
                const net = Number(count.summary?.netValue) || 0;
                return (
                  <tr key={count.countId} className="count-history-row">
                    <td>{formatDateTime(count.createdAt)}</td>
                    <td>{statusBadge(count.status)}</td>
                    <td className="num">{count.summary?.itemsCounted ?? 0}</td>
                    <td className="num">{count.summary?.itemsWithDifference ?? 0}</td>
                    <td className={`num ${kindClass(net)}`}>{formatCurrency(net)}</td>
                    <td className="count-history-note">{count.note || ''}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm count-view-detail"
                        onClick={() => openDetail(count.countId)}
                      >
                        {t('inventory.count.actions.viewDetail')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {historyLoading && <p className="count-muted">{t('inventory.count.loading')}</p>}
      {historyNextToken && !historyLoading && (
        <div className="count-load-more">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => loadHistory(historyNextToken)}
          >
            {t('inventory.count.actions.loadMore')}
          </button>
        </div>
      )}
    </div>
  );

  const renderDetailModal = () => {
    if (!detail) return null;
    const { count } = detail;
    return (
      <div className="modal-overlay" onClick={() => setDetail(null)}>
        <div
          className="modal-content modal-content--wide count-detail-modal"
          role="dialog"
          aria-modal="true"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <h2>
              {count
                ? t('inventory.count.detail.title', { date: formatDateTime(count.createdAt) })
                : t('inventory.count.detail.loading')}
            </h2>
            <button
              type="button"
              className="close-btn"
              onClick={() => setDetail(null)}
              aria-label={t('inventory.count.actions.close')}
            >
              <X size={18} />
            </button>
          </div>
          <div className="modal-body">
            {detail.loading && <p className="count-muted">{t('inventory.count.detail.loading')}</p>}
            {detail.error && (
              <div className="alert alert-error">{t('inventory.count.detail.loadError')}</div>
            )}
            {count && (
              <>
                <div className="count-detail-meta">
                  {statusBadge(count.status)}
                  {count.note && <span className="count-detail-note">{count.note}</span>}
                </div>
                <CountSummary count={count} t={t} />
                <h3 className="count-section-title">{t('inventory.count.detail.lines')}</h3>
                <div className="count-table-wrapper">
                  <table className="count-table count-detail-table">
                    <thead>
                      <tr>
                        <th>{t('inventory.count.columns.item')}</th>
                        <th>{t('inventory.count.columns.category')}</th>
                        <th className="num">{t('inventory.count.columns.theoretical')}</th>
                        <th className="num">{t('inventory.count.columns.counted')}</th>
                        <th className="num">{t('inventory.count.columns.difference')}</th>
                        <th className="num">{t('inventory.count.columns.differenceValue')}</th>
                        <th>{t('inventory.count.columns.reason')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(count.lines || []).map((line) => (
                        <tr key={line.itemId} className="count-detail-row">
                          <td>{line.name || line.itemId}</td>
                          <td>{line.category || t('inventory.count.uncategorized')}</td>
                          <td className="num">
                            {formatQuantity(line.theoretical)} {line.unit || ''}
                          </td>
                          <td className="num">
                            {formatQuantity(line.counted)} {line.unit || ''}
                          </td>
                          <td className={`num ${kindClass(line.difference)}`}>
                            {formatQuantity(line.difference, { signed: true })}
                          </td>
                          <td className={`num ${kindClass(line.differenceValue)}`}>
                            {line.differenceValue === null || line.differenceValue === undefined
                              ? '—'
                              : formatCurrency(line.differenceValue)}
                          </td>
                          <td>{line.reason || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setDetail(null)}>
              {t('inventory.count.actions.close')}
            </button>
            <button
              type="button"
              className="btn btn-primary count-export-csv"
              onClick={() => exportCount(count)}
              disabled={!count}
            >
              <Download size={16} />
              {t('inventory.count.actions.exportCsv')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const withDifference = confirmOpen ? linesWithDifference(countableItems, payload.lines) : 0;

  return (
    <div className="inventory-count-page">
      <div className="count-header">
        <button
          type="button"
          className="btn btn-ghost btn-sm count-back"
          onClick={() => guardNavigation(() => navigate('/inventory'))}
        >
          <ArrowLeft size={16} />
          {t('inventory.count.back')}
        </button>
        <h1>{t('inventory.count.title')}</h1>
        <p className="count-subtitle">{t('inventory.count.subtitle')}</p>
      </div>

      {feedback && (
        <div
          className={`alert alert-${feedback.type === 'error' ? 'error' : 'success'}`}
          role="status"
        >
          {feedback.message}
        </div>
      )}

      <div className="count-tabs" role="tablist">
        {canWrite && (
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'count'}
            className={`count-tab${tab === 'count' ? ' is-active' : ''}`}
            onClick={() => setTab('count')}
          >
            <ClipboardCheck size={16} />
            {t('inventory.count.tabs.count')}
          </button>
        )}
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'history'}
          className={`count-tab${tab === 'history' ? ' is-active' : ''}`}
          onClick={() => setTab('history')}
        >
          <History size={16} />
          {t('inventory.count.tabs.history')}
        </button>
      </div>

      {/* El conteo en curso se mantiene montado al ir al historial para no perder lo tipeado */}
      <div className="count-panel" hidden={tab !== 'count'}>
        {canWrite && renderCountTab()}
      </div>
      {tab === 'history' && renderHistoryTab()}

      {confirmOpen && (
        <div className="modal-overlay" onClick={() => !submitting && setConfirmOpen(false)}>
          <div
            className="modal-content count-confirm-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>{t('inventory.count.confirm.title')}</h2>
            </div>
            <div className="modal-body">
              <p className="count-confirm-body">
                {t('inventory.count.confirm.body', { count: payload.lines.length })}
              </p>
              <p>{t('inventory.count.confirm.withDifference', { count: withDifference })}</p>
              <p className="count-muted">{t('inventory.count.confirm.hint')}</p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setConfirmOpen(false)}
                disabled={submitting}
              >
                {t('inventory.count.actions.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary count-confirm-submit"
                onClick={handleConfirmSubmit}
                disabled={submitting}
              >
                {submitting
                  ? t('inventory.count.submitting')
                  : t('inventory.count.actions.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {renderDetailModal()}

      {showModal && (
        <div className="modal-overlay" onClick={cancelNavigation}>
          <div
            className="modal-content count-unsaved-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>{t('inventory.count.unsaved.title')}</h2>
            </div>
            <div className="modal-body">
              <p>{t('inventory.count.unsaved.body')}</p>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-ghost" onClick={cancelNavigation}>
                {t('inventory.count.unsaved.stay')}
              </button>
              <button type="button" className="btn btn-secondary" onClick={confirmNavigation}>
                {t('inventory.count.unsaved.leave')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  if (saveDraft()) confirmNavigation();
                }}
              >
                {t('inventory.count.unsaved.saveAndLeave')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InventoryCountPage;
